import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ReceevitLogoCompact } from '../common/ReceevitLogo';
import { 
  CheckCircle, 
  ArrowRight, 
  Star, 
  Users, 
  Shield, 
  Zap, 
  BarChart3, 
  CreditCard,
  MessageCircle,
  FileText,
  TrendingUp,
  Globe,
  Lock,
  Clock,
  Headphones,
  Play,
  ChevronRight,
  DollarSign,
  AlertTriangle,
  Link2
} from 'lucide-react';

const HomePage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const features = [
    {
      icon: <FileText className="w-8 h-8" aria-label="Invoice management icon" />,
      title: "Smart Invoice Management",
      description: "Automated invoice creation, smart workflows, and intelligent approval systems that save you hours every week.",
      color: "from-blue-500 to-blue-600"
    },
    {
      icon: <CreditCard className="w-8 h-8" aria-label="Payment icon" />,
      title: "Automated Payments",
      description: "Get paid 10x faster with automated payment collection, multiple payment methods, and real-time reconciliation.",
      color: "from-green-500 to-green-600"
    },
    {
      icon: <BarChart3 className="w-8 h-8" aria-label="Analytics icon" />,
      title: "Cash Flow Analytics",
      description: "Predict cash flow issues before they happen with 30-day forecasting and interactive dashboards.",
      color: "from-purple-500 to-purple-600"
    },
    {
      icon: <MessageCircle className="w-8 h-8" aria-label="Chat icon" />,
      title: "Real-time Collaboration",
      description: "Built-in chat system for invoice discussions, file sharing, and seamless team collaboration.",
      color: "from-pink-500 to-pink-600"
    },
    {
      icon: <Shield className="w-8 h-8" aria-label="Security icon" />,
      title: "Enterprise Security",
      description: "Bank-level security with PCI DSS compliance, end-to-end encryption, and fraud detection.",
      color: "from-red-500 to-red-600"
    },
    {
      icon: <Globe className="w-8 h-8" aria-label="Global banking icon" />,
      title: "Open Banking Integration",
      description: "Direct bank integration for faster payments and real-time account visibility across platforms.",
      color: "from-teal-500 to-teal-600"
    }
  ];


  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "CFO",
      company: "TechFlow Solutions",
      content: "Receevit® has transformed our invoice management. The AI insights have saved us hours every week and improved our cash flow by 40%.",
      rating: 5,
      avatar: "SJ"
    },
    {
      name: "Michael Chen",
      role: "Operations Director", 
      company: "Global Manufacturing Co",
      content: "The real-time chat feature has eliminated all communication delays with our suppliers. Our approval process is now 5x faster.",
      rating: 5,
      avatar: "MC"
    },
    {
      name: "Emma Rodriguez",
      role: "Finance Manager",
      company: "Innovation Labs",
      content: "Finally, a platform that makes B2B invoicing actually enjoyable to use! The automated payments feature is a game-changer.",
      rating: 5,
      avatar: "ER"
    }
  ];

  const howItWorks = [
    {
      step: "1",
      title: "Send & Receive Invoices",
      description: "Upload or create invoices in seconds, send them directly to clients, and receive invoices from your own suppliers — all in one secure portal.",
      icon: <FileText className="w-8 h-8" aria-label="Invoice document icon" />
    },
    {
      step: "2", 
      title: "Approve, Reject, or Auto-Approve",
      description: "Review invoices with a click. Approve, reject with a reason, or let the system auto-approve after 7 days — no more chasing or email back-and-forth.",
      icon: <MessageCircle className="w-8 h-8" aria-label="Approval chat icon" />
    },
    {
      step: "3",
      title: "Get Paid Automatically",
      description: "Approved invoices are scheduled for payment on the due date. With built-in chat and early payment incentives, clients pay faster and disputes are resolved quickly.",
      icon: <CreditCard className="w-8 h-8" aria-label="Payment processing icon" />
    }
  ];

  const benefits = [
    {
      icon: <Zap className="w-6 h-6" aria-label="Automation icon" />,
      title: "No More Chasing Payments",
      description: "Receevit® automates approvals and collections, so you spend less time following up and more time growing your business."
    },
    {
      icon: <BarChart3 className="w-6 h-6" aria-label="Growth icon" />,
      title: "Faster Cash Flow For Your Business", 
      description: "With built-in incentives for early payments and auto-collection on due dates, you get paid quicker and more consistently."
    },
    {
      icon: <Clock className="w-6 h-6" aria-label="Time saving icon" />,
      title: "One Platform for All Invoices",
      description: "Act as both a customer and a supplier in the same portal — send, receive, approve, and chat about invoices without juggling multiple tools."
    },
    {
      icon: <Lock className="w-6 h-6" aria-label="Relationship icon" />,
      title: "Stronger Client Relationships",
      description: "In-app chat replaces messy email chains, making payment discussions transparent, professional, and easy to resolve."
    }
  ];


  return (
    <>
      <Helmet>
        <title>Receevit® - Reduce Late Payments | B2B Credit Control Automation</title>
        <meta name="description" content="Automated B2B credit control with smart reminders, Stripe integration, and real-time chat. Reduce late payments and improve cash flow. Free 30-day trial." />
        <link rel="canonical" href="https://www.receevit.com/" />
        <meta property="og:title" content="Receevit® - Reduce Late Payments | B2B Credit Control Automation" />
        <meta property="og:description" content="Automated B2B credit control with smart reminders, Stripe integration, and real-time chat. Reduce late payments and improve cash flow. Free 30-day trial." />
        <meta property="og:url" content="https://www.receevit.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.receevit.com/og-image.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Receevit® - Reduce Late Payments | B2B Credit Control Automation",
            "url": "https://www.receevit.com/",
            "description": "Automated B2B credit control with smart reminders, Stripe integration, and real-time chat. Reduce late payments and improve cash flow. Free 30-day trial.",
            "publisher": {
              "@type": "Organization",
              "name": "Receevit®",
              "url": "https://www.receevit.com/"
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.receevit.com/"
              }
            ]
          })}
        </script>
      </Helmet>
      
      <div className="min-h-screen bg-[#1F11ED] relative overflow-hidden">

        {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#1F11ED]/95 backdrop-blur-xl border-b border-white/20 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <ReceevitLogoCompact size={160} forBlueBackground={true} />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-white hover:text-blue-200 transition-colors duration-200 font-medium">
                Home
              </Link>
              <Link to="/features" className="text-white hover:text-blue-200 transition-colors duration-200 font-medium">
                Features
              </Link>
              <Link to="/pricing" className="text-white hover:text-blue-200 transition-colors duration-200 font-medium">
                Pricing
              </Link>
              <Link to="/about" className="text-white hover:text-blue-200 transition-colors duration-200 font-medium">
                About
              </Link>
              <Link to="/support" className="text-white hover:text-blue-200 transition-colors duration-200 font-medium">
                Support
              </Link>
            </div>

            {/* CTA Buttons */}
            <div className="hidden md:flex items-center space-x-4">
              <Link
                to="/login"
                className="text-white hover:text-blue-200 transition-colors duration-200 font-medium px-4 py-2 rounded-lg hover:bg-white/10"
              >
                Login
              </Link>
              <Link
                to="/signup"
                className="bg-white text-[#1F11ED] px-6 py-2 rounded-xl font-semibold hover:bg-blue-50 hover:shadow-lg transition-all duration-300"
              >
                Get Started
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-white hover:text-blue-200 transition-colors duration-200"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-[#1F11ED]/95 border-t border-white/20"
            >
              <div className="px-2 pt-2 pb-3 space-y-1">
                <Link
                  to="/"
                  className="block px-3 py-2 text-white hover:text-blue-200 transition-colors duration-200 font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Home
                </Link>
                <Link
                  to="/features"
                  className="block px-3 py-2 text-white hover:text-blue-200 transition-colors duration-200 font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Features
                </Link>
                <Link
                  to="/pricing"
                  className="block px-3 py-2 text-white hover:text-blue-200 transition-colors duration-200 font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Pricing
                </Link>
                <Link
                  to="/about"
                  className="block px-3 py-2 text-white hover:text-blue-200 transition-colors duration-200 font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  About
                </Link>
                <Link
                  to="/support"
                  className="block px-3 py-2 text-white hover:text-blue-200 transition-colors duration-200 font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Support
                </Link>
                <div className="pt-4 space-y-2">
                  <Link
                    to="/login"
                    className="block px-3 py-2 text-white hover:text-blue-200 transition-colors duration-200 font-medium"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Login
                  </Link>
                  <Link
                    to="/signup"
                    className="block px-3 py-2 bg-white text-[#1F11ED] rounded-lg font-semibold text-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Get Started
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-20 pb-16 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#1F11ED]/10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-8"
            >
              <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-white/20 text-white border border-white/30">
                <Zap className="w-4 h-4 mr-2" aria-label="Speed icon" />
                Faster payment by your clients
              </span>
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight"
            >
              Transform Credit Control with{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-blue-200">Receevit®</span>
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg md:text-xl text-blue-100 mb-8 max-w-4xl mx-auto leading-relaxed"
            >
              Receevit® is the smarter way to manage credit control. 
              With built-in chat, automated invoice approvals, auto-collection on due dates, and customer incentives for early payments — chasing clients is a thing of the past.
            </motion.p>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8"
            >
              <Link
                to="/signup"
                className="group bg-white text-[#1F11ED] hover:bg-gray-100 px-8 py-4 rounded-xl text-base font-semibold transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                Start 30-Day Free Trial
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" aria-label="Arrow right" />
              </Link>
            </motion.div>
            
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-sm text-white"
            >
              Free for 30 days • Cancel anytime • Access to all features
            </motion.p>
          </div>
        </div>
      </section>


      {/* Real Cost of Late Payments Section */}
      <section className="py-20 relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              The Real Cost of Late Payments
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Late payments are a major challenge for businesses. Here's the reality:
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Time Spent Chasing */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-white" aria-label="Time icon" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Time Spent Chasing</h3>
              </div>
              <div className="text-center mb-6">
                <div className="text-3xl font-bold text-orange-400 mb-2">56.4M</div>
                <p className="text-blue-100">hours annually chasing invoices</p>
              </div>
              <div className="bg-blue-500/20 p-4 rounded-lg">
                <p className="text-blue-100 text-sm">
                  <strong>Receevit® solution:</strong> Automated approvals after 7 days and built-in chat eliminate the need for manual follow-ups.
                </p>
              </div>
            </motion.div>

            {/* Financial Impact */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white text-2xl font-bold">£</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Financial Impact</h3>
              </div>
              <div className="text-center mb-6">
                <div className="text-3xl font-bold text-red-400 mb-2">£11B</div>
                <p className="text-blue-100">lost annually to late payments</p>
                <p className="text-blue-200 text-sm mt-1">£22,000 average per small business</p>
              </div>
              <div className="bg-green-500/20 p-4 rounded-lg">
                <p className="text-green-100 text-sm">
                  <strong>Receevit® solution:</strong> Automatic payment scheduling on due dates ensures predictable cash flow.
                </p>
              </div>
            </motion.div>

            {/* Business Continuity Risks */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Business Closures</h3>
              </div>
              <div className="text-center mb-6">
                <div className="text-3xl font-bold text-purple-400 mb-2">38</div>
                <p className="text-blue-100">businesses close daily in the UK</p>
                <p className="text-blue-200 text-sm mt-1">due to late payments</p>
              </div>
              <div className="bg-purple-500/20 p-4 rounded-lg">
                <p className="text-purple-100 text-sm">
                  <strong>Receevit® solution:</strong> Early payment incentives and transparent communication motivate faster payments.
                </p>
              </div>
            </motion.div>

            {/* Stay In Control */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <BarChart3 className="w-8 h-8 text-white" aria-label="Analytics icon" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Overdue Invoices</h3>
              </div>
              <div className="text-center mb-6">
                <div className="text-3xl font-bold text-blue-400 mb-2">40%</div>
                <p className="text-blue-100">of B2B invoices are overdue</p>
              </div>
              <div className="bg-blue-500/20 p-4 rounded-lg">
                <p className="text-blue-100 text-sm">
                  <strong>Receevit® solution:</strong> Real-time dashboard with status indicators keeps you in complete control.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works Section - Clean & Simple */}
      <section className="py-20 relative">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              How It Works
            </h2>
            <p className="text-lg text-blue-100 max-w-2xl mx-auto">
              Get started with Receevit® in three simple steps
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
              {howItWorks.map((step, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                className="text-center"
                >
                {/* Step Number */}
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-[#1F11ED] font-bold text-xl mx-auto mb-6 shadow-lg">
                      {step.step}
            </div>
            
                {/* Icon */}
                <div className="w-16 h-16 bg-[#1F11ED] rounded-xl flex items-center justify-center mx-auto mb-6">
                      <div className="text-white text-2xl">
                        {step.icon}
                      </div>
                    </div>
                
                {/* Content */}
                <h3 className="text-xl font-bold text-white mb-4">
                      {step.title}
                    </h3>
                <p className="text-blue-100 leading-relaxed">
                      {step.description}
                    </p>
                </motion.div>
              ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Why Choose Receevit®?
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Discover the key benefits that make Receevit® the perfect solution for your business
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative"
              >
                <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm p-8 rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-500 hover:-translate-y-2 text-center">
                  <div className="bg-gradient-to-br from-[#1F11ED] to-blue-600 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <div className="text-white text-xl">
                      {benefit.icon}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4 group-hover:text-blue-200 transition-colors duration-300">
                    {benefit.title}
                  </h3>
                  <p className="text-blue-100 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>




      {/* Accounting Integration Section */}
      <section className="py-20 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-white/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-2xl mb-6">
              <Link2 className="w-10 h-10 text-white" aria-label="Integration icon" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Seamless Accounting Integration
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Connect with QuickBooks and Xero in seconds. Post transactions directly to your accounting software with just one click — no manual data entry required.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* How Easy It Is */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center mr-4">
                  <Zap className="w-6 h-6 text-white" aria-label="Easy icon" />
                </div>
                <h3 className="text-2xl font-bold text-white">It's That Simple</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-400 mr-3 flex-shrink-0 mt-0.5" aria-label="Check icon" />
                  <div>
                    <p className="text-white font-semibold mb-1">One-Click Connection</p>
                    <p className="text-blue-100 text-sm">Connect your QuickBooks or Xero account with a single click. No technical setup required.</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-400 mr-3 flex-shrink-0 mt-0.5" aria-label="Check icon" />
                  <div>
                    <p className="text-white font-semibold mb-1">One-Click Sync</p>
                    <p className="text-blue-100 text-sm">Sync invoices to your accounting software whenever you're ready with a single click. You're in complete control.</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-400 mr-3 flex-shrink-0 mt-0.5" aria-label="Check icon" />
                  <div>
                    <p className="text-white font-semibold mb-1">No Duplicates</p>
                    <p className="text-blue-100 text-sm">Smart duplicate detection ensures your accounting software stays clean and accurate.</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-400 mr-3 flex-shrink-0 mt-0.5" aria-label="Check icon" />
                  <div>
                    <p className="text-white font-semibold mb-1">Choose Your Account Codes</p>
                    <p className="text-blue-100 text-sm">Select the correct account codes and categories when syncing, ensuring your books are organised exactly how you need them.</p>
                  </div>
                </li>
              </ul>
            </motion.div>

            {/* Benefits */}
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center mr-4">
                  <TrendingUp className="w-6 h-6 text-white" aria-label="Benefits icon" />
                </div>
                <h3 className="text-2xl font-bold text-white">Save Hours Every Week</h3>
              </div>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <span className="text-blue-300 font-bold">1</span>
                  </div>
                  <div>
                    <p className="text-white font-semibold mb-1">Eliminate Manual Entry</p>
                    <p className="text-blue-100 text-sm">Stop copying invoice data between systems. One-click syncing transfers all the details instantly.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <span className="text-blue-300 font-bold">2</span>
                  </div>
                  <div>
                    <p className="text-white font-semibold mb-1">Reduce Errors</p>
                    <p className="text-blue-100 text-sm">Direct syncing means no typos, no missing invoices, no reconciliation headaches.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <span className="text-blue-300 font-bold">3</span>
                  </div>
                  <div>
                    <p className="text-white font-semibold mb-1">Stay Compliant</p>
                    <p className="text-blue-100 text-sm">All transactions are recorded with proper account codes and categories when you sync.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <span className="text-blue-300 font-bold">4</span>
                  </div>
                  <div>
                    <p className="text-white font-semibold mb-1">Accessible to Everyone</p>
                    <p className="text-blue-100 text-sm">Available on both Essential and Pro plans — no upgrade needed.</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-white/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-2xl md:text-3xl font-bold text-white mb-6"
          >
            Ready to Transform Your B2B Credit Control?
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-xl text-blue-100 mb-8"
          >
            Join forward-thinking businesses using Receevit® to modernise their credit control management
          </motion.p>
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              to="/signup"
              className="bg-white text-[#1F11ED] hover:bg-gray-100 px-8 py-4 rounded-xl text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              Start Free Trial
            </Link>
            <Link
              to="/support"
              className="border-2 border-white text-white hover:bg-white hover:text-[#1F11ED] px-8 py-4 rounded-xl text-lg font-semibold transition-all duration-300 text-center"
            >
              Schedule Demo
            </Link>
          </motion.div>
        </div>
      </section>

      </div>
    </>
  );
};

export default HomePage;
